import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Button, Input, Alert, Empty, Tag, Popconfirm, Row, Col, Statistic,
  Typography, Space, Tooltip, theme,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ArrowRightOutlined, BankOutlined,
  EnvironmentOutlined, ClockCircleOutlined, FolderOutlined,
  CheckCircleOutlined, FlagOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { getUserProjects, getOrgProjects, getAssignedProjects, createProject, deleteProject } from '../firebase/firestore';
import { Project } from '../models/types';

/* AntD preset tag colors per project status */
const STATUS_TAG: Record<string, string> = {
  active: 'green', completed: 'blue', archived: 'default',
};

const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const { orgId, organization, isAdmin, isContractor, isLoading: orgLoading } = useOrg();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  /* `slow` gates the spinner: the cache-first read resolves in milliseconds on a revisit, and
     flashing a loading card for one frame then re-rendering was a visible stutter on every
     navigation. Nothing is drawn in the list area until the data lands or 300 ms pass. */
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newClient, setNewClient] = useState('');
  const [newDesc, setNewDesc] = useState('');

  /* Wait for OrgContext (auth token live, and orgId/isContractor settled so the right query runs),
     then read cache-first: the projects list paints instantly from the local cache — no server
     round-trip on a weak field connection — and updates in the background when the server responds
     (apply is the onRefresh). A fresh install with nothing cached falls back to a retryable server
     read inside the helper. */
  useEffect(() => {
    if (!currentUser || orgLoading) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    const slowTimer = setTimeout(() => { if (!cancelled) setSlow(true); }, 300);
    const apply = (p: Project[]) => { if (!cancelled) setProjects(p); };
    // Contractors get only their assigned projects (rules deny them the org-wide query);
    // otherwise show org projects if in an org, else personal projects.
    const fetch = isContractor
      ? getAssignedProjects(currentUser.uid, apply)
      : orgId ? getOrgProjects(orgId, apply) : getUserProjects(currentUser.uid, apply);
    fetch
      .then(apply)
      .catch(() => { if (!cancelled) setError('Failed to load projects'); })
      .finally(() => { clearTimeout(slowTimer); if (!cancelled) { setLoading(false); setSlow(false); } });
    return () => { cancelled = true; clearTimeout(slowTimer); };
  }, [currentUser, orgId, isContractor, orgLoading]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !newName.trim()) return;
    setCreating(true);
    try {
      const id = await createProject(currentUser.uid, newName.trim(), newAddress.trim(), newDesc.trim(), orgId ?? undefined, newClient.trim());
      setShowForm(false); setNewName(''); setNewAddress(''); setNewClient(''); setNewDesc('');
      navigate(`/projects/${id}`);
    } catch { setError('Failed to create project'); setCreating(false); }
  };

  // Deletion is creator-or-org-admin (mirrors firestore.rules). An org project is deletable by
  // its creator or by an admin of the same org; a personal project only by its creator.
  const canDelete = (p: Project) =>
    p.userId === currentUser?.uid || (isAdmin && !!p.orgId && p.orgId === orgId);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try { await deleteProject(id); setProjects(p => p.filter(x => x.id !== id)); }
    catch (e) {
      setError((e as { code?: string })?.code === 'permission-denied'
        ? 'You do not have permission to delete this project. Only its creator or an org admin can.'
        : 'Failed to delete project');
    }
    finally { setDeletingId(null); }
  };

  const formatDate = (iso: string) => { try { return new Date(iso).toLocaleDateString(); } catch { return '—'; } };

  const total = projects.length;
  const activeCount = projects.filter(p => p.status === 'active').length;
  const completedCount = projects.filter(p => p.status === 'completed').length;

  const field = (label: string, node: React.ReactNode) => (
    <div>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{label}</div>
      {node}
    </div>
  );

  return (
    <div>
      {/* Page head */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>Projects</Typography.Title>
            <Typography.Text type="secondary">
              {organization ? <><BankOutlined /> {organization.name}</> : 'Your projects'}
              {!loading && <> · {total} {total === 1 ? 'project' : 'projects'}</>}
            </Typography.Text>
          </div>
          {!isContractor && (
            <Button
              type="primary" icon={<PlusOutlined />} style={{ marginLeft: 'auto' }}
              onClick={() => setShowForm(s => !s)}
            >
              New Project
            </Button>
          )}
        </div>
      </Card>

      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} style={{ marginBottom: 16 }} />}

      {!loading && total > 0 && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col flex="1 1 0">
            <Card styles={{ body: { padding: '20px 24px' } }}>
              <Statistic title="Total projects" value={total} prefix={<FolderOutlined style={{ color: token.colorPrimary }} />} />
            </Card>
          </Col>
          <Col flex="1 1 0">
            <Card styles={{ body: { padding: '20px 24px' } }}>
              <Statistic title="Active" value={activeCount} prefix={<CheckCircleOutlined style={{ color: token.colorSuccess }} />} />
            </Card>
          </Col>
          <Col flex="1 1 0">
            <Card styles={{ body: { padding: '20px 24px' } }}>
              <Statistic title="Completed" value={completedCount} prefix={<FlagOutlined style={{ color: token.colorWarning }} />} />
            </Card>
          </Col>
        </Row>
      )}

      {showForm && (
        <Card title="New project" style={{ marginBottom: 16 }}>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 }}>
              {field('Project name *', (
                <Input
                  value={newName} onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. 123 Main St Office" required autoFocus
                />
              ))}
              {field('Address', (
                <Input value={newAddress} onChange={e => setNewAddress(e.target.value)} placeholder="Building address" />
              ))}
              {field('Client', (
                <Input value={newClient} onChange={e => setNewClient(e.target.value)} placeholder="Who the report is prepared for" />
              ))}
            </div>
            {field('Description', (
              <Input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Optional notes" />
            ))}
            <Space style={{ marginTop: 16 }}>
              <Button type="primary" htmlType="submit" disabled={creating || !newName.trim()}>
                {creating ? 'Creating…' : 'Create project'}
              </Button>
              <Button onClick={() => setShowForm(false)}>Cancel</Button>
            </Space>
          </form>
        </Card>
      )}

      {loading ? (
        // Blank while a fast cache read is in flight; spinner only once it's proven slow.
        slow ? <Card loading /> : null
      ) : total === 0 ? (
        <Card>
          <Empty
            description={(
              <>
                <Typography.Title level={5}>{isContractor ? 'No projects assigned yet' : 'No projects yet'}</Typography.Title>
                <Typography.Paragraph type="secondary">
                  {isContractor
                    ? 'An administrator will assign projects to you. They will appear here.'
                    : 'Create your first project to get started.'}
                </Typography.Paragraph>
              </>
            )}
          >
            {!isContractor && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowForm(true)}>Create project</Button>
            )}
          </Empty>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {projects.map(p => (
            <Col xs={24} md={12} xl={8} key={p.id}>
              <Card
                styles={{ body: { padding: '16px 20px' } }}
                actions={[
                  <Button type="text" icon={<ArrowRightOutlined />} onClick={() => navigate(`/projects/${p.id}`)}>
                    Open
                  </Button>,
                  canDelete(p) ? (
                    <Popconfirm
                      title={`Delete project "${p.name}"?`}
                      description="This cannot be undone."
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleDelete(p.id)}
                    >
                      <Button
                        type="text" danger icon={<DeleteOutlined />}
                        loading={deletingId === p.id} aria-label="Delete project"
                      />
                    </Popconfirm>
                  ) : (
                    <Tooltip title="Only the project's creator or an org admin can delete it">
                      <Button type="text" icon={<DeleteOutlined />} disabled aria-label="Delete project" />
                    </Tooltip>
                  ),
                ]}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <BankOutlined style={{ fontSize: 18, color: token.colorPrimary }} />
                  <Typography.Text strong ellipsis style={{ flex: 1 }}>{p.name}</Typography.Text>
                  <Tag color={STATUS_TAG[p.status] ?? 'default'} style={{ marginInlineEnd: 0 }}>{p.status}</Tag>
                </div>
                {p.address && (
                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}><EnvironmentOutlined /> {p.address}</Typography.Text>
                  </div>
                )}
                {p.description && (
                  <Typography.Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ fontSize: 13, marginBottom: 4 }}>
                    {p.description}
                  </Typography.Paragraph>
                )}
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  <ClockCircleOutlined /> Updated {formatDate(p.updatedAt)}
                </Typography.Text>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default Dashboard;
